import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import {
  Mutation,
  MutationAddVisitorArgs,
  MutationAddVisitorsToVisitorGroupArgs,
  MutationSetVisitorByIdArgs,
  Query,
  QueryGetVisitorByIdArgs,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import {
  ADD_VISITOR_WITH_ROLE,
  ADD_VISITORS_TO_VISITOR_GROUP,
  SET_VISITOR_BY_ID_WITH_ROLE
} from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_DETAILS
} from 'graphql/v2/queries';
import { TranslatedHookProps } from 'hooks/shared/types';
import { useEffect, useMemo } from 'react';
import { refetchQueries } from 'v2/hooks/useAllVisitors/useAllVisitors';
import useUserFormVisitorGroups from 'v2/hooks/useUserFormVisitorGroups/useUserFormVisitorGroups';
import { updateVisitorGroupMembershipWithAdminGroup } from './utils';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> =
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS;
const GET_VISITOR_DETAILS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorById'>,
  QueryGetVisitorByIdArgs
> = GET_VISITOR_DETAILS;
const ADD_VISITOR_WITH_ROLE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addVisitor'>,
  MutationAddVisitorArgs
> = ADD_VISITOR_WITH_ROLE;
const SET_VISITOR_BY_ID_WITH_ROLE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'setVisitorById'>,
  MutationSetVisitorByIdArgs
> = SET_VISITOR_BY_ID_WITH_ROLE;
const ADD_VISITORS_TO_VISITOR_GROUP_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addVisitorsToVisitorGroup'>,
  MutationAddVisitorsToVisitorGroupArgs
> = ADD_VISITORS_TO_VISITOR_GROUP;

// The member counts are based on `memberIds`, so the slim groups query is refetched as well.
const addOrEditVisitorRefetchQueries = [...refetchQueries, { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }];

const useAddVisitor = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const { coContractors, administratorsVisitorGroups, installersVisitorGroups, visitorGroupsLoading } =
    useUserFormVisitorGroups({ handleFetchError, translation });

  // Only used for the duplicate email check. Emails are unique across all users (the backend rejects any existing
  // one), so every user is checked, not only the members of the co-contractor groups.
  const { data: visitorsData, error: visitorsError } = useQuery(GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (visitorsError) {
      handleFetchError(translation.users_error_fetching);
      console.error(visitorsError);
    }
  }, [visitorsError]);

  const existingEmails = useMemo(
    () => (visitorsData?.getVisitors ?? []).map(({ email }) => email).filter((email): email is string => !!email),
    [visitorsData]
  );

  const [fetchVisitorDetails] = useLazyQuery(GET_VISITOR_DETAILS_TYPED);

  const [addVisitorMutation, { loading: addVisitorLoading }] = useMutation(ADD_VISITOR_WITH_ROLE_TYPED, {
    refetchQueries: addOrEditVisitorRefetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      if (error.message === 'Visitor already exists') handleFetchError(translation.user_email_already_exists);
      else if (error.message === 'Visitor is not only CO_CONTRACTOR')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_adding);
    }
  });

  const [updateVisitorMutation, { loading: updateVisitorLoading }] = useMutation(SET_VISITOR_BY_ID_WITH_ROLE_TYPED, {
    refetchQueries: addOrEditVisitorRefetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      if (error.message === 'Visitor is not only CO_CONTRACTOR')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_updating);
    }
  });

  type AddOrEditVisitorArgs = {
    isNewVisitor: boolean;
    name: string;
    visitorGroupMembershipInput: VisitorGroupMembershipInput[];
    shouldBeAdmin: boolean;
    shouldBeInstaller: boolean;
    email?: string;
    id?: string;
    expirationDate?: dayjs.Dayjs;
  };

  const addOrEditVisitor = async ({
    isNewVisitor,
    name,
    shouldBeAdmin,
    shouldBeInstaller,
    email,
    id,
    expirationDate,
    visitorGroupMembershipInput
  }: AddOrEditVisitorArgs) => {
    // The save sends the user's full membership list. The ATLAS group memberships come from the form as group ids, but
    // the Administrator and Installer roles come as on/off flags: each role is a membership of the Administrators or
    // Installers group, so those two groups' ids are needed to add or remove it. A group's id never changes, so the ids
    // are read from the groups already loaded instead of fetching the groups again. The member ids that do change are
    // refreshed by the mutation's refetch of the slim groups query.
    if (administratorsVisitorGroups === undefined || installersVisitorGroups === undefined) {
      handleFetchError(translation.external_groups_user_error_updating);
      return undefined;
    }
    if (id) {
      try {
        await fetchVisitorDetails({ variables: { id } });
      } catch {
        handleFetchError(translation.external_groups_user_error_updating);
        return undefined;
      }
    }

    let result;
    const expirationDateIso = expirationDate ? dayjs(expirationDate).toISOString() : undefined;
    const membershipWithExpirationDate = expirationDateIso
      ? (visitorGroupMembershipInput || []).map((membership) => ({
          ...membership,
          expirationDate: expirationDateIso
        }))
      : visitorGroupMembershipInput || [];

    const updatedVisitorGroupMembership: VisitorGroupMembershipInput[] = updateVisitorGroupMembershipWithAdminGroup({
      visitorGroupMembership: membershipWithExpirationDate,
      options: {
        shouldBeAdmin,
        adminGroup: administratorsVisitorGroups[0],
        shouldBeInstaller,
        installerGroup: installersVisitorGroups[0]
      }
    });

    try {
      if (isNewVisitor && email) {
        result = await addVisitorMutation({
          variables: {
            name,
            email,
            visitorGroupMembership: updatedVisitorGroupMembership
          },
          // Don't let a refetch failure reject the mutation promise — the visitor has
          // already been created on the backend at this point, and a rejected promise
          // would leave the drawer stuck open even though the add succeeded. Refetches
          // still fire (via refetchQueries above) to keep the list fresh in the background.
          awaitRefetchQueries: false
        });
      } else if (id) {
        result = await updateVisitorMutation({
          variables: {
            name,
            visitorId: id,
            visitorGroupMembership: updatedVisitorGroupMembership
          },
          refetchQueries: [...addOrEditVisitorRefetchQueries, { query: GET_VISITOR_DETAILS, variables: { id } }],
          awaitRefetchQueries: false
        });
      }
    } catch {
      // error is already surfaced via the mutations' onError above; we want to avoid an unhandled rejection
    }
    return result;
  };

  const [addVisitorsToVisitorGroupMutation, { loading: addVisitorsToVisitorGroupLoading }] = useMutation(
    ADD_VISITORS_TO_VISITOR_GROUP_TYPED,
    {
      // The mutation result only returns the group's `members`, not its `memberIds`, those are two different fields and the member counts are based
      // on `memberIds`. Refetch the slim groups query so those counts include the newly added users, and the slim visitors
      // query so each added user's memberships are up to date.
      refetchQueries: [
        { query: GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS },
        { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }
      ],
      awaitRefetchQueries: true
    }
  );

  const addVisitorsToVisitorGroup = async ({ memberships, visitorGroupId }: MutationAddVisitorsToVisitorGroupArgs) => {
    const result = await addVisitorsToVisitorGroupMutation({
      variables: {
        memberships,
        visitorGroupId
      }
    });
    return result;
  };

  return {
    coContractors,
    existingEmails,
    visitorGroupsLoading,
    administratorsVisitorGroups,
    installersVisitorGroups,
    addOrEditVisitor,
    addOrEditVisitorLoading: addVisitorLoading || updateVisitorLoading || visitorGroupsLoading,
    addVisitorsToVisitorGroup,
    addVisitorsToVisitorGroupLoading
  };
};

export default useAddVisitor;
